/*
 * Шаги курса «Вокал с нуля» поверх MusicCore (захват, часы графа, метроном,
 * задержка) и детектора голоса /vocal-range:
 *   voice-range     — удобный низ и верх голоса (или тип голоса кнопкой);
 *                     всё, что поётся дальше, транспонируется в него;
 *   pitch-hold      — попасть в ноту и держать: живая линия, центы, ровность
 *                     и вибрато числами;
 *   pitch-follow    — спеть мелодию под метроном, вердикт по каждой ноте;
 *                     take:true — дубль можно прослушать и сохранить;
 *   pitch-slide     — сирена: охват в полутонах без разрывов;
 *   breath-sustain  — ровный выдох на «с-с-с» в секундах (без микрофона —
 *                     секундомер);
 *   voice-rhythm    — слог на каждую долю ритма (без микрофона — касание).
 *
 * Высоту голоса судит НЕ гитарный детектор MusicCore.pitchStream, а
 * PitchDetect с порогами, замеренными на 233 записях VocalSet для /vocal-range
 * (строгий выбор {0.93, 0.95}, ворота −54 dBFS, «на 20 дБ тише певца — не
 * певец», устойчивый показ ноты VocalCapture.makeNoteDisplay) — память
 * reference_singing_pitch_lab_2026_10_01. Чистые функции судейства —
 * window.SingVoice: их же гоняет стенд tests/js/course-singing.test.mjs.
 * Числа — docs/why/singing-course.md.
 */
(function (root) {
    'use strict';

    var SV = {};
    var PD = root.PitchDetect, VC = root.VocalCapture;

    /* ── пороги (замер — docs/why/singing-course.md) ────────────────── */
    SV.DET = { fmin: 50, searchFmax: 2400, minClarity: 0.5, strict: { cutoff: 0.93, clarity: 0.95 } };
    SV.LIMITS = {
        N: 2048,                 // окно детектора (как у /vocal-range)
        MIN_RMS: 0.002,          // −54 dBFS: тише — не голос
        LOCK_CLARITY: 0.62,      // чёткость, с которой нота «держится»
        HOLD_TOL: 40,            // нота засчитана ближе стольких центов
        HOLD_GAP_MS: 140,        // провал короче не рвёт удержание
        HOLD_TIMEOUT_MS: 25000,  // на одну ноту — столько, потом дальше
        FOLLOW_TOL: 50,          // нота мелодии: ±50 ¢
        FOLLOW_VOICED: 0.35,     // доля окна ноты, где голос есть
        FOLLOW_INTUNE: 0.5,      // доля звучащих кадров в допуске
        SLIDE_GAP_MS: 180,       // разрыв сирены длиннее — новый кусок
        BREATH_MARGIN_DB: 10,    // выдох громче тишины на столько
        BREATH_GAP_MS: 300,      // провал выдоха короче — тот же выдох
        BREATH_END_MS: 700,      // тишина дольше после выдоха — конец
        VIBRATO_MIN_HZ: 4,
        VIBRATO_MAX_HZ: 8,
        VIBRATO_MIN_CENTS: 30,   // полуразмах; у обученных певцов ±50–100 ¢
        VIBRATO_R: 0.55          // автокорреляция «пульса» на его периоде (стенд VocalSet)
    };
    /* Удобные диапазоны по типам голоса (грудь, без напряжения), MIDI. */
    SV.TYPES = {
        bass: [43, 60],      // G2–C4
        tenor: [48, 65],     // C3–F4
        alto: [53, 72],      // F3–C5
        soprano: [57, 77],   // A3–F5
        child: [57, 74]      // A3–D5
    };

    var SHARPS = ['C', 'C#', 'D', 'D#', 'E', 'F', 'F#', 'G', 'G#', 'A', 'A#', 'B'];
    function freqToMidi(f) { return 69 + 12 * Math.log2(f / 440); }
    function midiName(m) { var n = Math.round(m); return SHARPS[((n % 12) + 12) % 12] + (Math.floor(n / 12) - 1); }
    SV.midiName = midiName;
    SV.freqToMidi = freqToMidi;

    /* Тоника упражнений — кварта над удобным низом, но так, чтобы октава
       над ней ещё помещалась в удобный верх, если диапазон это позволяет. */
    SV.homeOf = function (r) {
        if (!r) return 55;
        var h = r.low + 5;
        if (h + 12 > r.high) h = Math.max(r.low + 2, r.high - 12);
        return Math.round(h);
    };
    SV.typeOf = function (low, high) {
        var best = null, bd = 1e9;
        Object.keys(SV.TYPES).forEach(function (k) {
            if (k === 'child') return;
            var t = SV.TYPES[k], d = Math.abs(t[0] - low) + Math.abs(t[1] - high) * 0.6;
            if (d < bd) { bd = d; best = k; }
        });
        return best;
    };

    /* ── кадр → нота (чистая функция над окном 2048) ────────────────── */
    function rmsOf(x) { var s = 0; for (var i = 0; i < x.length; i++) s += x[i] * x[i]; return Math.sqrt(s / x.length); }
    SV.rms = rmsOf;
    /*
     * FrameEngine(sr) — то же, что делает /vocal-range с кадром: ворота по
     * громкости (абсолютные и «20 дБ под певцом»), детектор MPM со строгим
     * выбором, чёткость удержания. push(frame, tMs) → {midi, rms, clarity} | null.
     * opts.deaf — кадр не певца (звучит наш эталон): уровень певца не трогаем.
     */
    function FrameEngine(sr) {
        this.sr = sr;
        this.level = VC && VC.makeSingerLevel ? VC.makeSingerLevel() : null;
    }
    FrameEngine.prototype.push = function (x, tMs, deaf) {
        var L = SV.LIMITS;
        var r = rmsOf(x);
        var gate = Math.max(L.MIN_RMS, this.level && !deaf ? this.level.gate(tMs) : 0);
        if (r < gate || !PD) return null;
        var d = PD.detect(x, this.sr, SV.DET);
        if (!d || d.clarity < L.LOCK_CLARITY) return null;
        var m = freqToMidi(d.freq);
        if (m < 28 || m > 96) return null;
        if (this.level && !deaf) this.level.hear(r, d.clarity, tMs);
        return { midi: m, rms: r, clarity: d.clarity, freq: d.freq };
    };
    SV.FrameEngine = FrameEngine;

    /* ── удержание ноты ─────────────────────────────────────────────── */
    /*
     * HoldJudge(target, tol, needMs): push(tMs, midi|null) → состояние.
     * Удержание — подряд в допуске; провал короче HOLD_GAP_MS не рвёт.
     * samples — центы каждого кадра удержания: из них ровность и вибрато.
     */
    function HoldJudge(target, tol, needMs) {
        this.target = target;
        this.tol = tol || SV.LIMITS.HOLD_TOL;
        this.need = needMs || 1500;
        this.runStart = null; this.lastIn = null; this.best = 0; this.done = false;
        this.samples = []; this.bestSamples = [];
    }
    HoldJudge.prototype.push = function (t, midi) {
        var c = midi == null ? null : (midi - this.target) * 100;
        var inTune = c != null && Math.abs(c) <= this.tol;
        if (inTune) {
            if (this.runStart == null || t - this.lastIn > SV.LIMITS.HOLD_GAP_MS) { this.runStart = t; this.samples = []; }
            this.lastIn = t;
            this.samples.push({ t: t, c: c });
            var run = t - this.runStart;
            if (run > this.best) { this.best = run; this.bestSamples = this.samples.slice(); }
            if (run >= this.need) this.done = true;
        } else if (this.lastIn != null && t - this.lastIn > SV.LIMITS.HOLD_GAP_MS) {
            this.runStart = null;
        }
        return { cents: c, inTune: inTune, run: this.runStart == null ? 0 : (this.lastIn - this.runStart), best: this.best, done: this.done };
    };
    SV.HoldJudge = HoldJudge;

    /*
     * Ровность и вибрато по центам удержания [{t, c}]: sd — разброс после
     * снятия медленного дрейфа (скользящее среднее 300 мс), вибрато —
     * автокорреляция в полосе 4–8 Гц и размах (полуразмах) в центах.
     */
    SV.steadiness = function (samples) {
        if (!samples || samples.length < 12) return null;
        var n = samples.length;
        var dt = (samples[n - 1].t - samples[0].t) / (n - 1);
        if (!(dt > 0)) return null;
        var c = samples.map(function (s) { return s.c; });
        var mean = c.reduce(function (a, b) { return a + b; }, 0) / n;
        var w = Math.max(1, Math.round(300 / dt / 2)), slow = [];
        for (var i = 0; i < n; i++) {
            var a = 0, k = 0;
            for (var j = Math.max(0, i - w); j <= Math.min(n - 1, i + w); j++) { a += c[j]; k++; }
            slow.push(a / k);
        }
        var fast = c.map(function (v, i) { return v - slow[i]; });
        var sdAll = Math.sqrt(c.reduce(function (a, v) { return a + (v - mean) * (v - mean); }, 0) / n);
        var sdFast = Math.sqrt(fast.reduce(function (a, v) { return a + v * v; }, 0) / n);
        var drift = Math.max.apply(null, slow) - Math.min.apply(null, slow);
        var vib = null;
        var lo = Math.round(1000 / SV.LIMITS.VIBRATO_MAX_HZ / dt), hi = Math.round(1000 / SV.LIMITS.VIBRATO_MIN_HZ / dt);
        if (n > hi * 2) {
            var e0 = fast.reduce(function (a, v) { return a + v * v; }, 0) || 1, bestR = 0, bestLag = 0;
            for (var lag = Math.max(2, lo); lag <= hi; lag++) {
                var s = 0;
                for (var q = 0; q + lag < n; q++) s += fast[q] * fast[q + lag];
                var r = s / e0 * n / (n - lag);
                if (r > bestR) { bestR = r; bestLag = lag; }
            }
            var extent = sdFast * Math.SQRT2;
            if (bestR >= SV.LIMITS.VIBRATO_R && extent >= SV.LIMITS.VIBRATO_MIN_CENTS) vib = { rate: 1000 / (bestLag * dt), extent: extent, r: bestR };
        }
        return { mean: mean, sd: sdAll, sdFast: sdFast, drift: drift, vibrato: vib };
    };

    /* ── мелодия ────────────────────────────────────────────────────── */
    /*
     * judgeFollow(notes [{midi, t0, t1}], frames [{t (с), midi|null}], lagS):
     * окно ноты — её середина (20 %…90 % длительности), сдвинутая на
     * задержку устройства. hit — голос звучал ≥ FOLLOW_VOICED окна и
     * ≥ FOLLOW_INTUNE звучащих кадров в ±FOLLOW_TOL; octave — то же, но
     * ровно на октаву (засчитывается наполовину: мелодия верна, регистр нет).
     */
    SV.judgeFollow = function (notes, frames, lagS) {
        var L = SV.LIMITS, lag = lagS || 0;
        var out = notes.map(function (nt) {
            var d = nt.t1 - nt.t0;
            var a = nt.t0 + 0.2 * d + lag, b = nt.t0 + 0.9 * d + lag;
            var all = 0, voiced = 0, inT = 0, octT = 0, cents = [];
            frames.forEach(function (f) {
                if (f.t < a || f.t > b) return;
                all++;
                if (f.midi == null) return;
                voiced++;
                var c = (f.midi - nt.midi) * 100;
                cents.push(c);
                if (Math.abs(c) <= L.FOLLOW_TOL) inT++;
                else if (Math.abs(Math.abs(c) - 1200) <= L.FOLLOW_TOL) octT++;
            });
            cents.sort(function (p, q) { return p - q; });
            var med = cents.length ? cents[cents.length >> 1] : null;
            var state = 'silent';
            if (all && voiced / all >= L.FOLLOW_VOICED) {
                if (inT / voiced >= L.FOLLOW_INTUNE) state = 'hit';
                else if (octT / voiced >= L.FOLLOW_INTUNE) state = 'octave';
                else state = 'miss';
            }
            return { state: state, med: med, voiced: all ? voiced / all : 0 };
        });
        var pts = out.reduce(function (s, r) { return s + (r.state === 'hit' ? 1 : r.state === 'octave' ? 0.5 : 0); }, 0);
        return { notes: out, score: notes.length ? pts / notes.length : 0 };
    };

    /* ── сирена ─────────────────────────────────────────────────────── */
    /* judgeSlide(frames [{t, midi|null}]) → самый длинный связный кусок голоса:
       охват (полутона между 5-м и 95-м перцентилем — без выбросов) и
       плавность (доля шагов меньше 0.8 полутона между соседними кадрами). */
    SV.judgeSlide = function (frames) {
        var segs = [], cur = [], last = -1e9;
        frames.forEach(function (f) {
            if (f.midi == null) return;
            if (f.t * 1000 - last > SV.LIMITS.SLIDE_GAP_MS && cur.length) { segs.push(cur); cur = []; }
            cur.push(f);
            last = f.t * 1000;
        });
        if (cur.length) segs.push(cur);
        var best = null, bestSpan = 0;
        segs.forEach(function (s) {
            if (s.length < 6) return;
            var m = s.map(function (f) { return f.midi; }).sort(function (a, b) { return a - b; });
            var lo = m[Math.floor(m.length * 0.05)], hi = m[Math.min(m.length - 1, Math.floor(m.length * 0.95))];
            var smooth = 0;
            for (var i = 1; i < s.length; i++) if (Math.abs(s[i].midi - s[i - 1].midi) < 0.8) smooth++;
            var span = hi - lo;
            if (span > bestSpan) { bestSpan = span; best = { span: span, low: lo, high: hi, smooth: smooth / (s.length - 1), dur: s[s.length - 1].t - s[0].t }; }
        });
        return best || { span: 0, low: null, high: null, smooth: 0, dur: 0 };
    };

    /* ── выдох ──────────────────────────────────────────────────────── */
    /* BreathMeter(floorRms): push(tMs, rms) → {active, dur (мс), best, stable}.
       Выдох — громче тишины на BREATH_MARGIN_DB; провал короче BREATH_GAP_MS —
       тот же выдох. stable — разброс громкости (дБ) в середине выдоха. */
    function BreathMeter(floor) {
        this.th = Math.max(0.0015, floor * Math.pow(10, SV.LIMITS.BREATH_MARGIN_DB / 20));
        this.start = null; this.last = null; this.best = 0; this.db = []; this.bestDb = [];
    }
    BreathMeter.prototype.push = function (t, r) {
        var on = r >= this.th;
        if (on) {
            if (this.start == null || t - this.last > SV.LIMITS.BREATH_GAP_MS) { this.start = t; this.db = []; }
            this.last = t;
            this.db.push(20 * Math.log10(r));
            var dur = t - this.start;
            if (dur > this.best) { this.best = dur; this.bestDb = this.db.slice(); }
        }
        var active = this.start != null && t - this.last <= SV.LIMITS.BREATH_GAP_MS;
        var ended = this.start != null && t - this.last > SV.LIMITS.BREATH_END_MS && this.best >= 1000;
        return { active: active, dur: active ? this.last - this.start : 0, best: this.best, ended: ended };
    };
    BreathMeter.prototype.stability = function () {
        var d = this.bestDb;
        if (d.length < 20) return null;
        var mid = d.slice(Math.floor(d.length * 0.15), Math.ceil(d.length * 0.85));
        var m = mid.reduce(function (a, b) { return a + b; }, 0) / mid.length;
        return Math.sqrt(mid.reduce(function (a, v) { return a + (v - m) * (v - m); }, 0) / mid.length);
    };
    SV.BreathMeter = BreathMeter;

    /* ── эталонный звук: стопка гармоник (синус на мужском низу в колонках
       телефона не слышен — память reference_singing_pitch_lab) ───────── */
    var HARM = [1, 0.62, 0.42, 0.26, 0.17, 0.11, 0.07, 0.045];
    SV.tone = function (ctx, midi, when, dur, gain) {
        var f = 440 * Math.pow(2, (midi - 69) / 12);
        var out = ctx.createGain();
        var g = gain == null ? 0.16 : gain;
        out.gain.setValueAtTime(0.0001, when);
        out.gain.exponentialRampToValueAtTime(g, when + 0.06);
        out.gain.setValueAtTime(g, when + Math.max(0.07, dur - 0.18));
        out.gain.exponentialRampToValueAtTime(0.0001, when + dur);
        out.connect(ctx.destination);
        HARM.forEach(function (a, k) {
            var fk = f * (k + 1);
            if (fk > 9000) return;
            var o = ctx.createOscillator(), og = ctx.createGain();
            o.type = 'sine';
            o.frequency.value = fk;
            og.gain.value = a / 2.6;
            o.connect(og); og.connect(out);
            o.start(when); o.stop(when + dur + 0.05);
        });
        return when + dur;
    };
    SV.melody = function (ctx, seq, bpm, gain) {
        var spb = 60 / (bpm || 80), t = ctx.currentTime + 0.08;
        seq.forEach(function (n) {
            var d = (n.beats || 1) * spb;
            if (n.midi != null) SV.tone(ctx, n.midi, t, Math.max(0.12, d * 0.92), gain);
            t += d;
        });
        return t;
    };

    /* Сдвиг ударов на медиану их отклонений от ожидаемых долей (ближайший
       удар в окне −150…+450 мс; меньше трёх — не сдвигаем). */
    SV.alignByMedian = function (expected, onsets) {
        var offs = [];
        expected.forEach(function (e) {
            var best = null;
            onsets.forEach(function (x) { var d = x - e; if (d > -0.15 && d < 0.45 && (best === null || Math.abs(d) < Math.abs(best))) best = d; });
            if (best !== null) offs.push(best);
        });
        if (offs.length < 3) return onsets.slice();
        offs.sort(function (a, b) { return a - b; });
        var m = offs[offs.length >> 1];
        return onsets.map(function (x) { return x - m; });
    };

    root.SingVoice = SV;
    if (typeof module !== 'undefined' && module.exports) module.exports = SV;

    /* ════════════════════ шаги курса (только в браузере) ════════════════ */
    var K = root.CourseKit, MC = root.MusicCore;
    if (!K || !MC || typeof document === 'undefined') return;
    MC.install(K);
    /* Эталон для голоса — тянущийся тон, а не щипок струны: певец подстраивается
       к звучащей ноте, а щипок гаснет через секунду. */
    K.audio.notes = function (notes, o) {
        o = o || {};
        return K.util.audioCtx().then(function (ctx) {
            var list = (Array.isArray(notes) ? notes : [notes]).map(function (x) { return typeof x === 'number' ? x : MC.parseNote(x); });
            if (o.mode === 'chord') {
                var now = ctx.currentTime + 0.06, d = o.dur || 1.8;
                list.forEach(function (m) { if (m != null) SV.tone(ctx, m, now, d, 0.1); });
                return new Promise(function (r) { setTimeout(r, d * 1000 + 80); });
            }
            var end = SV.melody(ctx, list.map(function (m) { return { midi: m, beats: 1 }; }), o.bpm || 92);
            return new Promise(function (r) { setTimeout(r, Math.max(0, (end - ctx.currentTime) * 1000)); });
        });
    };

    function T(step) { return step.t || {}; }
    /* Число с одним знаком после запятой — разделителем языка страницы. */
    function dec1(x) {
        var v = (Math.round(x * 10) / 10).toFixed(1);
        try { var sep = (1.5).toLocaleString(root.__intlLocale ? root.__intlLocale() : undefined).charAt(1); if (sep && sep !== '.') v = v.replace('.', sep); } catch (_) {}
        return v;
    }
    /* Ступень от домашней ноты слогом («подвижное до» уроков): до, ре, ми… */
    function degName(api, m, home) {
        var arr = api.tt('degrees');
        arr = typeof arr === 'string' && arr.indexOf(',') > 0 ? arr.split(',') : null;
        if (!arr || home == null) return null;
        var d = ((Math.round(m) - home) % 12 + 12) % 12;
        return arr[d] || null;
    }
    function solfa(api, m) {
        var s = api.tt('solfege');
        var arr = Array.isArray(s) ? s : (K.util && typeof s === 'string' && s.indexOf(',') > 0 ? s.split(',') : null);
        var n = Math.round(m), pc = ((n % 12) + 12) % 12;
        return midiName(n) + (arr && arr[pc] ? ' · ' + arr[pc] : '');
    }
    function noteLabel(api, m, home) {
        var d = degName(api, m, home);
        return d ? d + ' · ' + midiName(m) : midiName(m);
    }
    function centsWord(api, c) {
        var a = Math.abs(Math.round(c));
        if (a <= 10) return api.tt('s_on_pitch');
        return c > 0 ? api.tt('s_sharp', { c: a }) : api.tt('s_flat', { c: a });
    }

    /* ── диапазон пользователя ──────────────────────────────────────── */
    function rangeOf(api) {
        var v = api.store.get('voice', null);
        return v && typeof v.low === 'number' && typeof v.high === 'number' ? v : null;
    }
    /* Если диапазона нет (человек открыл урок мимо первого), шаг сначала
       спрашивает тип голоса — пять кнопок, без микрофона. */
    function needRange(host, api, then) {
        var r = rangeOf(api);
        if (r) { then(r); return; }
        var box = api.el('div', 'sg-types');
        box.appendChild(api.richEl('p', 'crs-hint', api.tt('s_need_range')));
        typeChips(box, api, function (rr) { box.remove(); then(rr); });
        host.appendChild(box);
    }
    function typeChips(box, api, done) {
        var row = api.el('div', 'crs-chips');
        Object.keys(SV.TYPES).forEach(function (k) {
            var b = api.el('button', 'crs-chip', api.tt('s_type_' + k));
            b.type = 'button';
            b.addEventListener('click', function () {
                var r = { low: SV.TYPES[k][0], high: SV.TYPES[k][1], kind: k };
                api.store.set('voice', r);
                done(r);
            });
            row.appendChild(b);
        });
        box.appendChild(row);
    }

    /* ── микрофон ───────────────────────────────────────────────────── */
    /*
     * micPanel(host, api, onReady, opts): «Включить микрофон», уровень и строка
     * состояния; отказ — фраза причины, повтор и «Пропустить» (api.skip — не
     * ошибка). opts.alt = {label, fn} — путь без микрофона.
     */
    function micPanel(host, api, onReady, opts) {
        opts = opts || {};
        var box = api.el('div', 'sg-mic');
        var meter = api.el('div', 'sg-meter');
        var bar = api.el('span');
        meter.appendChild(bar);
        var status = api.el('p', 'sg-status');
        status.setAttribute('aria-live', 'polite');
        status.textContent = api.tt('s_mic_hint');
        box.appendChild(meter);
        box.appendChild(status);
        host.appendChild(box);
        var raf = 0, L = null;
        function loop() {
            if (!api.alive() || !L) return;
            var db = 20 * Math.log10(L.level() + 1e-9);
            bar.style.width = Math.max(0, Math.min(100, (db + 60) / 50 * 100)) + '%';
            raf = requestAnimationFrame(loop);
        }
        api.onCleanup(function () { if (raf) cancelAnimationFrame(raf); });
        function start() {
            api.primary(api.tt('s_mic_start'), null);
            status.textContent = api.tt('s_mic_wait');
            MC.listen(api).then(function (listener) {
                if (!api.alive()) { listener.stop(); return; }
                L = listener;
                box.classList.add('is-on');
                status.textContent = api.tt('s_mic_on');
                raf = requestAnimationFrame(loop);
                api.secondary(null, null);
                onReady(listener);
            }, function (err) {
                if (!api.alive()) return;
                status.textContent = api.micError(err);
                box.classList.add('is-off');
                api.primary(api.tt('s_mic_retry'), start);
                if (opts.alt) api.secondary(opts.alt.label, opts.alt.fn);
                else api.secondary(api.tt('s_no_mic_skip'), function () { api.skip(api.tt('s_skipped')); });
            });
        }
        api.primary(api.tt('s_mic_start'), start);
        if (opts.alt) api.secondary(opts.alt.label, opts.alt.fn);
        else api.secondary(api.tt('s_no_mic_skip'), function () { api.skip(api.tt('s_skipped')); });
        return {
            setStatus: function (s, cls) { status.textContent = s; status.className = 'sg-status' + (cls ? ' ' + cls : ''); },
            el: box
        };
    }

    /* Поток нот голоса: fn(reading|null, tMs, rms). deaf() — сейчас звучит наш
       эталон: его кадры не голос певца. */
    function voiceStream(L, fn, deaf) {
        var eng = new FrameEngine(L.sr);
        return L.onBlock(function (pcm, frame) {
            var tMs = (frame + pcm.length) / L.sr * 1000;
            var x = L.latest(SV.LIMITS.N);
            var mute = deaf && deaf(tMs / 1000);
            var r = eng.push(x, tMs, mute);
            fn(mute ? null : r, tMs, rmsOf(x));
        });
    }

    /* ── картинка: линия высоты над целями ──────────────────────────── */
    function PitchView(host, api, o) {
        o = o || {};
        var wrap = api.el('div', 'sg-view');
        var cv = document.createElement('canvas');
        cv.className = 'sg-canvas';
        cv.setAttribute('role', 'img');
        cv.setAttribute('aria-label', api.tt('s_view_label'));
        wrap.appendChild(cv);
        host.appendChild(wrap);
        var H = o.height || 190, dpr = 1, W = 300;
        var pts = [], held = null, targets = o.targets || [], center = o.center || 60, semis = o.semis || 7;
        var colors = null;
        function css() {
            var cs = getComputedStyle(wrap);
            var g = function (v, d) { var x = cs.getPropertyValue(v); return x && x.trim() ? x.trim() : d; };
            colors = { text: g('--lg-text-3', '#8899aa'), grid: g('--lg-border', 'rgba(140,150,170,.25)'), acc: g('--lg-accent', '#7aa7ff'), ok: g('--lg-success', '#34d399'), warn: g('--lg-warn', '#fbbf24') };
        }
        function resize() {
            dpr = Math.min(2, root.devicePixelRatio || 1);
            W = Math.max(240, wrap.clientWidth || 300);
            cv.width = Math.round(W * dpr); cv.height = Math.round(H * dpr);
            cv.style.height = H + 'px';
        }
        resize(); css();
        var ro = null, lastArgs = null;
        /* Смена ширины холста стирает его: неподвижную картинку (превью
           мелодии, итог) рисуем заново, живую перерисует свой цикл кадров. */
        try { ro = new ResizeObserver(function () { resize(); if (lastArgs) V.draw.apply(null, lastArgs); }); ro.observe(wrap); } catch (_) {}
        api.onCleanup(function () { if (ro) ro.disconnect(); });
        var V = {
            setCenter: function (m) { center = m; },
            setTargets: function (t) { targets = t || []; },
            clear: function () { pts = []; },
            /* Одиночный кадр дальше 3 полутонов от соседей (щелчок, стык,
               согласная) на рисунок не идёт: он рисовался иглой через всю шкалу.
               Судейство его не касается — там медиана окна. */
            push: function (t, m, ok) {
                var last = pts.length ? pts[pts.length - 1] : null;
                if (m != null && last && last.m != null && t - last.t < 120 && Math.abs(m - last.m) > 3) {
                    if (!held || Math.abs(m - held.m) > 1) { held = { t: t, m: m, ok: ok }; return; }
                    pts.push(held);
                }
                held = null;
                pts.push({ t: t, m: m, ok: ok });
                if (pts.length > 1500) pts.splice(0, pts.length - 1500);
            },
            /* draw(now) — бегущее окно o.seconds; drawSpan(a, b, cur) — неподвижная шкала */
            draw: function (now, fixed) {
                lastArgs = [now, fixed];
                var c = cv.getContext('2d');
                if (!c) return;
                c.setTransform(dpr, 0, 0, dpr, 0, 0);
                c.clearRect(0, 0, W, H);
                var lo = center - semis, hi = center + semis;
                var y = function (m) { return H - 10 - (m - lo) / (hi - lo) * (H - 20); };
                var x0, x1;
                if (fixed) { x0 = fixed.a; x1 = fixed.b; } else { x1 = now; x0 = now - (o.seconds || 6) * 1000; }
                var x = function (t) { return 36 + (t - x0) / (x1 - x0) * (W - 44); };
                c.font = '12px system-ui, sans-serif';
                c.textBaseline = 'middle';
                for (var m = Math.ceil(lo); m <= Math.floor(hi); m++) {
                    var nat = [0, 2, 4, 5, 7, 9, 11].indexOf(((m % 12) + 12) % 12) >= 0;
                    c.strokeStyle = colors.grid;
                    c.globalAlpha = nat ? 0.9 : 0.4;
                    c.beginPath(); c.moveTo(36, y(m)); c.lineTo(W - 4, y(m)); c.stroke();
                    c.globalAlpha = 1;
                    if (nat) { c.fillStyle = colors.text; c.fillText(midiName(m), 2, y(m)); }
                }
                targets.forEach(function (tg) {
                    var half = (tg.tol || SV.LIMITS.HOLD_TOL) / 100;
                    var ya = y(tg.midi + half), yb = y(tg.midi - half);
                    var xa = tg.t0 != null ? x(tg.t0) : 36, xb = tg.t1 != null ? x(tg.t1) : W - 4;
                    c.fillStyle = tg.state === 'hit' ? colors.ok : tg.state === 'miss' || tg.state === 'silent' ? colors.warn : colors.acc;
                    c.globalAlpha = tg.state ? 0.35 : 0.22;
                    c.fillRect(xa, ya, Math.max(2, xb - xa), Math.max(3, yb - ya));
                    c.globalAlpha = 1;
                });
                var prev = null;
                pts.forEach(function (p) {
                    if (p.t < x0 || p.t > x1 || p.m == null) { prev = null; return; }
                    var px = x(p.t), py = y(Math.max(lo - 0.5, Math.min(hi + 0.5, p.m)));
                    c.strokeStyle = p.ok ? colors.ok : colors.warn;
                    c.lineWidth = 3;
                    if (prev && p.t - prev.t < 120) { c.beginPath(); c.moveTo(prev.x, prev.y); c.lineTo(px, py); c.stroke(); }
                    else { c.fillStyle = c.strokeStyle; c.fillRect(px - 1.5, py - 1.5, 3, 3); }
                    prev = { x: px, y: py, t: p.t };
                });
                if (fixed && fixed.cur != null) {
                    c.strokeStyle = colors.acc; c.lineWidth = 2;
                    c.beginPath(); c.moveTo(x(fixed.cur), 4); c.lineTo(x(fixed.cur), H - 4); c.stroke();
                }
            }
        };
        return V;
    }

    function refButton(row, api, label, fn) {
        var b = api.el('button', 'lg-btn lg-btn-ghost lg-btn-sm', '▶ ' + label);
        b.type = 'button';
        b.addEventListener('click', function () { b.disabled = true; Promise.resolve(fn()).catch(function () {}).then(function () { b.disabled = false; }); });
        row.appendChild(b);
        return b;
    }

    /* ════ voice-range ════ */
    K.step('voice-range', {
        graded: false,
        mount: function (host, step, api) {
            var t = T(step);
            if (t.prompt) host.appendChild(api.richEl('p', 'crs-prompt', t.prompt));
            var saved = rangeOf(api);
            var info = api.el('p', 'sg-big', '—');
            var sub = api.el('p', 'sg-status');
            host.appendChild(info);
            host.appendChild(sub);
            var typesBox = api.el('div', 'sg-types');
            typesBox.appendChild(api.el('p', 'crs-hint', api.tt('s_or_type')));
            typeChips(typesBox, api, function (r) { finish(r, false); });
            function finish(r, measured) {
                var semi = r.high - r.low;
                var msg = api.tt('s_range_done', { low: solfa(api, r.low), high: solfa(api, r.high), n: semi });
                if (measured) msg += ' ' + api.tt('s_range_type', { type: api.tt('s_type_' + (SV.typeOf(r.low, r.high) || 'tenor')) });
                api.answer({ correct: null, explain: msg + '\n\n' + api.tt('s_range_home', { home: solfa(api, SV.homeOf(r)) }) });
            }
            if (saved && !step.force) {
                info.textContent = solfa(api, saved.low) + ' — ' + solfa(api, saved.high);
                sub.textContent = api.tt('s_range_saved');
                api.primary(api.tt('s_range_keep'), function () { finish(saved, false); });
                api.secondary(api.tt('s_range_again'), function () { host.innerHTML = ''; saved = null; api.secondary(null, null); measure(); });
                return;
            }
            measure();
            function measure() {
                if (!host.contains(info)) { if (t.prompt) host.appendChild(api.richEl('p', 'crs-prompt', t.prompt)); host.appendChild(info); host.appendChild(sub); }
                var view = PitchView(host, api, { center: 55, semis: 14, seconds: 6 });
                host.appendChild(typesBox);
                var phase = 'low', low = null, high = null, win = [], raf = 0;
                var disp = VC && VC.makeNoteDisplay ? VC.makeNoteDisplay() : null;
                function stableOf(now) {
                    while (win.length && win[0].t < now - 900) win.shift();
                    if (win.length < 12 || now - win[0].t < 750) return null;
                    var m = win.map(function (w) { return w.m; }).sort(function (a, b) { return a - b; });
                    var med = m[m.length >> 1];
                    for (var i = 0; i < m.length; i++) if (Math.abs(m[i] - med) > 0.7) return null;
                    return Math.round(med);
                }
                var panel = micPanel(host, api, function (L) {
                    sub.textContent = api.tt('s_range_low');
                    voiceStream(L, function (r, tMs) {
                        var shown = disp ? disp.step(tMs, r ? r.midi : null) : (r ? Math.round(r.midi) : null);
                        view.push(tMs, r ? r.midi : null, true);
                        if (r) {
                            win.push({ t: tMs, m: r.midi });
                            view.setCenter(Math.round(r.midi));
                            var st = stableOf(tMs);
                            if (st != null) {
                                if (phase === 'low' && (low == null || st < low)) { low = st; api.primary(api.tt('s_range_low_ok', { note: solfa(api, low) }), toHigh); }
                                if (phase === 'high' && (high == null || st > high)) { high = st; api.primary(api.tt('s_range_high_ok', { note: solfa(api, high) }), done); }
                            }
                        } else win = [];
                        info.textContent = shown != null ? solfa(api, shown) : '—';
                    });
                    function frame() { if (!api.alive()) return; view.draw(L.ctx.currentTime * 1000); raf = requestAnimationFrame(frame); }
                    raf = requestAnimationFrame(frame);
                    api.onCleanup(function () { cancelAnimationFrame(raf); });
                    api.primary(api.tt('s_range_wait'), function () {}, true);
                });
                function toHigh() {
                    phase = 'high'; win = [];
                    sub.textContent = api.tt('s_range_high', { low: solfa(api, low) });
                    api.primary(api.tt('s_range_wait'), function () {}, true);
                }
                function done() {
                    if (high - low < 5) { panel.setStatus(api.tt('s_range_narrow'), 'is-bad'); high = null; api.primary(api.tt('s_range_wait'), function () {}, true); return; }
                    var r = { low: low, high: high, kind: 'measured' };
                    api.store.set('voice', r);
                    finish(r, true);
                }
            }
        }
    });

    /* ════ pitch-hold ════ */
    /* data: degs [смещение от тоники] | deg, hold (с), tol (¢), steady, maxSd (¢) */
    K.step('pitch-hold', {
        reviewable: false,
        mount: function (host, step, api) {
            var t = T(step);
            if (t.prompt) host.appendChild(api.richEl('p', 'crs-prompt', t.prompt));
            needRange(host, api, function (range) {
                var home = SV.homeOf(range);
                var degs = step.degs || [step.deg || 0];
                var targets = degs.map(function (d) { return Math.max(range.low - 2, Math.min(range.high + 3, home + d)); });
                var need = (step.hold || 1.5) * 1000, tol = step.tol || SV.LIMITS.HOLD_TOL;
                var head = api.el('p', 'sg-big', '');
                var read = api.el('p', 'sg-status', '');
                var prog = api.el('div', 'sg-meter sg-hold');
                var pbar = api.el('span');
                prog.appendChild(pbar);
                host.appendChild(head);
                var view = PitchView(host, api, { center: targets[0], semis: 5, seconds: 5 });
                host.appendChild(prog);
                host.appendChild(read);
                var row = api.el('div', 'crs-acts');
                host.appendChild(row);
                var i = 0, judge = null, deafUntil = 0, startedAt = 0, results = [], L = null, raf = 0;
                function setTarget() {
                    var m = targets[i];
                    head.textContent = (targets.length > 1 ? api.tt('s_note_n', { n: i + 1, total: targets.length }) + ' ' : '') + noteLabel(api, m, home);
                    view.setCenter(m);
                    view.setTargets([{ midi: m, tol: tol }]);
                    view.clear();
                    judge = new SV.HoldJudge(m, tol, need);
                    pbar.style.width = '0%';
                }
                function playRef() {
                    return api.audioCtx().then(function (ctx) {
                        var d = 1.4;
                        deafUntil = ctx.currentTime + d + 0.25;
                        SV.tone(ctx, targets[i], ctx.currentTime + 0.05, d);
                        return new Promise(function (r) { setTimeout(r, d * 1000 + 100); });
                    });
                }
                setTarget();
                refButton(row, api, api.tt('s_listen_note'), playRef);
                micPanel(host, api, function (listener) {
                    L = listener;
                    read.textContent = api.tt('s_hold_go', { s: step.hold || 1.5 });
                    if (step.ref !== false) playRef();
                    startedAt = 0;
                    voiceStream(L, function (r, tMs) {
                        if (i >= targets.length) return;
                        if (!startedAt) startedAt = tMs;
                        var st = judge.push(tMs, r ? r.midi : null);
                        view.push(tMs, r ? r.midi : null, st.inTune);
                        if (r) read.textContent = solfa(api, r.midi) + ' · ' + centsWord(api, st.cents);
                        pbar.style.width = Math.min(100, st.run / need * 100) + '%';
                        if (st.done) next(true);
                        else if (tMs - startedAt > SV.LIMITS.HOLD_TIMEOUT_MS) next(false);
                    }, function (ts) { return ts < deafUntil; });
                    function frame() { if (!api.alive()) return; view.draw(L.ctx.currentTime * 1000); raf = requestAnimationFrame(frame); }
                    raf = requestAnimationFrame(frame);
                    api.onCleanup(function () { cancelAnimationFrame(raf); });
                    api.primary(api.tt('s_give_up'), function () { next(false); });
                });
                function next(ok) {
                    var stdy = SV.steadiness(judge.bestSamples);
                    results.push({ ok: ok, best: judge.best, st: stdy });
                    api.announce(ok ? api.tt('s_hold_ok') : api.tt('s_hold_miss'));
                    i++;
                    startedAt = 0;
                    if (i < targets.length) { setTarget(); if (step.ref !== false) playRef(); return; }
                    finish();
                }
                function finish() {
                    var pts = 0, lines = [];
                    results.forEach(function (r, k) {
                        var s = r.ok ? 1 : Math.min(0.7, r.best / need * 0.7);
                        if (r.ok && step.steady && r.st && step.maxSd && r.st.sd > step.maxSd) s *= 0.75;
                        pts += s;
                        var line = solfa(api, targets[k]) + ': ' + (r.ok ? api.tt('s_res_ok') : api.tt('s_res_best', { s: dec1(r.best / 1000) }));
                        if (r.st) {
                            line += ' · ' + api.tt('s_res_sd', { c: Math.round(r.st.sd) });
                            if (r.st.vibrato) line += ' · ' + api.tt('s_res_vib', { hz: dec1(r.st.vibrato.rate), c: Math.round(r.st.vibrato.extent) });
                        }
                        lines.push(line);
                    });
                    var all = results.every(function (r) { return r.ok; });
                    var ex = lines.join('\n') + (t.explain ? '\n\n' + t.explain : '');
                    api.answer({ correct: all, score: pts / results.length, explain: ex });
                }
            });
        }
    });

    /* ════ pitch-follow ════ */
    /* data: notes [смещение от тоники | null — пауза], beats [длительности в
       долях], bpm, pass, take (сохранить дубль) */
    K.step('pitch-follow', {
        reviewable: false,
        mount: function (host, step, api) {
            var t = T(step);
            if (t.prompt) host.appendChild(api.richEl('p', 'crs-prompt', t.prompt));
            needRange(host, api, function (range) {
                var home = SV.homeOf(range);
                var bpm = step.bpm || 72, spb = 60 / bpm;
                var beats = step.notes.map(function (_, k) { return (step.beats && step.beats[k]) || 1; });
                var seq = step.notes.map(function (d, k) { return { midi: d == null ? null : home + d, beats: beats[k] }; });
                var total = beats.reduce(function (a, b) { return a + b; }, 0);
                var mids = seq.filter(function (n) { return n.midi != null; }).map(function (n) { return n.midi; });
                var center = (Math.min.apply(null, mids) + Math.max.apply(null, mids)) / 2;
                var cellsRow = api.el('div', 'sg-notes');
                var cells = seq.map(function (n) {
                    var c = api.el('span', 'sg-note' + (n.midi == null ? ' is-rest' : ''));
                    c.textContent = n.midi == null ? '·' : (degName(api, n.midi, home) || midiName(n.midi));
                    if (n.midi != null && t.sing_on) c.setAttribute('title', t.sing_on);
                    cellsRow.appendChild(c);
                    return c;
                });
                host.appendChild(cellsRow);
                if (t.sing_on) host.appendChild(api.richEl('p', 'crs-hint', t.sing_on));
                var view = PitchView(host, api, { center: center, semis: Math.max(5, Math.ceil((Math.max.apply(null, mids) - Math.min.apply(null, mids)) / 2) + 3) });
                var status = api.el('p', 'sg-status', '');
                host.appendChild(status);
                var row = api.el('div', 'crs-acts');
                host.appendChild(row);
                var listenBtn = refButton(row, api, api.tt('s_listen_melody'), function () {
                    return api.audioCtx().then(function (ctx) { var end = SV.melody(ctx, seq, bpm); return new Promise(function (r) { setTimeout(r, (end - ctx.currentTime) * 1000 + 50); }); });
                });
                var L = null, frames = [], pcmTake = null, metro = null, raf = 0, last = null;
                api.onCleanup(function () { if (metro) metro.stop(); cancelAnimationFrame(raf); });
                function layout(t0) {
                    var acc = 0;
                    return seq.map(function (n) { var a = t0 + acc * spb; acc += n.beats; return { midi: n.midi, t0: a, t1: t0 + acc * spb }; });
                }
                view.setTargets(layout(0).filter(function (n) { return n.midi != null; }).map(function (n) { return { midi: n.midi, t0: n.t0 * 1000, t1: n.t1 * 1000, tol: SV.LIMITS.FOLLOW_TOL }; }));
                view.draw(0, { a: 0, b: total * spb * 1000 });
                micPanel(host, api, function (listener) {
                    L = listener;
                    status.textContent = api.tt('s_follow_ready', { bpm: bpm });
                    api.primary(api.tt('s_sing_now'), go);
                });
                function go() {
                    api.primary(null, null); api.secondary(null, null);
                    listenBtn.disabled = true;
                    frames = []; view.clear();
                    cells.forEach(function (c) { c.className = c.className.replace(/ is-(hit|miss|octave|silent|cur)/g, ''); });
                    var ctx = L.ctx;
                    metro = MC.metronome(ctx, { bpm: bpm, beats: 4, countIn: 4, bars: Math.ceil(total / 4) + 1, quietCount: false, volume: 0.35 });
                    var t0 = metro.start();
                    var lag = MC.latency.get(api, ctx).s;
                    var notes = layout(t0);
                    var real = notes.filter(function (n) { return n.midi != null; });
                    view.setTargets(real.map(function (n) { return { midi: n.midi, t0: (n.t0 + lag) * 1000, t1: (n.t1 + lag) * 1000, tol: SV.LIMITS.FOLLOW_TOL }; }));
                    var tEnd = t0 + total * spb + lag + 0.4;
                    var chunks = [];
                    var unsub = L.onBlock(function (pcm, frame) { if (step.take && frame / L.sr >= t0 - 0.3 && frame / L.sr <= tEnd) chunks.push(new Float32Array(pcm)); });
                    var unsubV = voiceStream(L, function (r, tMs) {
                        var ts = tMs / 1000;
                        if (ts < t0 - 0.2 || ts > tEnd) return;
                        frames.push({ t: ts, midi: r ? r.midi : null });
                        var cur = null;
                        notes.forEach(function (n) { if (ts - lag >= n.t0 && ts - lag < n.t1) cur = n; });
                        var ok = !!(r && cur && cur.midi != null && Math.abs((r.midi - cur.midi) * 100) <= SV.LIMITS.FOLLOW_TOL);
                        view.push(tMs, r ? r.midi : null, ok);
                    });
                    metro.onBeat(function (i) {
                        if (i < 0) { status.textContent = api.tt('s_count_in') + ' ' + (4 + i + 1); return; }
                        status.textContent = api.tt('s_singing');
                    });
                    function frame() {
                        if (!api.alive()) return;
                        var now = ctx.currentTime;
                        var heard = now - (root.AudibleClock ? root.AudibleClock.lagS(ctx) : 0);
                        var idx = -1, acc = 0;
                        for (var k = 0; k < seq.length; k++) { if (heard >= t0 + acc * spb && heard < t0 + (acc + seq[k].beats) * spb) idx = k; acc += seq[k].beats; }
                        cells.forEach(function (c, k) { c.classList.toggle('is-cur', k === idx); });
                        view.draw(now * 1000, { a: (t0 - 0.3) * 1000, b: (tEnd) * 1000, cur: (heard + lag) * 1000 });
                        if (now > tEnd + 0.15) { unsub(); unsubV(); metro.stop(); judge(notes, lag, chunks, t0, tEnd); return; }
                        raf = requestAnimationFrame(frame);
                    }
                    raf = requestAnimationFrame(frame);
                }
                function judge(notes, lag, chunks, t0, tEnd) {
                    listenBtn.disabled = false;
                    var real = notes.filter(function (n) { return n.midi != null; });
                    var r = SV.judgeFollow(real, frames, lag);
                    var k = 0;
                    seq.forEach(function (n, j) { if (n.midi == null) return; cells[j].classList.add('is-' + r.notes[k].state); k++; });
                    var tg = real.map(function (n, j) { return { midi: n.midi, t0: (n.t0 + lag) * 1000, t1: (n.t1 + lag) * 1000, tol: SV.LIMITS.FOLLOW_TOL, state: r.notes[j].state }; });
                    view.setTargets(tg);
                    view.draw(0, { a: (t0 - 0.3) * 1000, b: tEnd * 1000 });
                    var hits = r.notes.filter(function (x) { return x.state === 'hit'; }).length;
                    var oct = r.notes.filter(function (x) { return x.state === 'octave'; }).length;
                    var sil = r.notes.filter(function (x) { return x.state === 'silent'; }).length;
                    var lines = [api.tt('s_follow_res', { hits: hits, total: real.length })];
                    if (oct) lines.push(api.tt('s_follow_oct', { n: oct }));
                    if (sil > real.length / 2) lines.push(api.tt('s_follow_quiet'));
                    var drift = r.notes.filter(function (x) { return x.state === 'miss' && x.med != null; }).map(function (x) { return x.med; });
                    if (drift.length >= 2) {
                        var avg = drift.reduce(function (a, b) { return a + b; }, 0) / drift.length;
                        if (Math.abs(avg) > 60) lines.push(avg > 0 ? api.tt('s_follow_high') : api.tt('s_follow_low'));
                    }
                    last = { score: r.score, lines: lines };
                    status.textContent = lines.join(' ');
                    if (step.take && chunks.length) takeTools(chunks, L.sr);
                    var pass = step.pass || 0.7;
                    api.primary(api.tt('s_accept'), function () {
                        api.answer({ correct: last.score >= pass - 1e-9, score: last.score, explain: last.lines.join('\n') + (t.explain ? '\n\n' + t.explain : '') });
                    });
                    api.secondary(api.tt('s_again'), go);
                }
                var takeRow = null;
                function takeTools(chunks, sr) {
                    var n = chunks.reduce(function (a, c) { return a + c.length; }, 0);
                    var pcm = new Float32Array(n), o = 0;
                    chunks.forEach(function (c) { pcm.set(c, o); o += c.length; });
                    if (root.TakeLevel) {
                        var st = root.TakeLevel.analyze({ channels: [pcm], sampleRate: sr });
                        var g = root.TakeLevel.normalizeGainDb(st);
                        if (g) root.TakeLevel.applyGain({ channels: [pcm] }, g);
                    }
                    pcmTake = pcm;
                    if (takeRow) takeRow.remove();
                    takeRow = api.el('div', 'crs-acts');
                    host.appendChild(takeRow);
                    refButton(takeRow, api, api.tt('s_take_play'), function () {
                        return api.audioCtx().then(function (ctx) {
                            var b = ctx.createBuffer(1, pcmTake.length, sr);
                            b.getChannelData(0).set(pcmTake);
                            var s = ctx.createBufferSource(); s.buffer = b; s.connect(ctx.destination); s.start();
                            return new Promise(function (r) { s.onended = r; });
                        });
                    });
                    var save = api.el('button', 'lg-btn lg-btn-ghost lg-btn-sm', '⤓ ' + api.tt('s_take_save'));
                    save.type = 'button';
                    save.addEventListener('click', function () {
                        var blob = wavOf(pcmTake, sr);
                        if (root.__downloadFile) root.__downloadFile(blob, api.tt('s_take_file') + '.wav');
                    });
                    takeRow.appendChild(save);
                }
            });
        }
    });
    /* Моно 16 бит — тот же формат, что AudioPipeline.audioBufferToWav для одного
       канала; общий модуль весит ~100 КБ ради одной кнопки, поэтому здесь
       двадцать строк записи заголовка. */
    function wavOf(pcm, sr) {
        var n = pcm.length, head = new ArrayBuffer(44), v = new DataView(head);
        function s(o, str) { for (var i = 0; i < str.length; i++) v.setUint8(o + i, str.charCodeAt(i)); }
        s(0, 'RIFF'); v.setUint32(4, 36 + n * 2, true); s(8, 'WAVE'); s(12, 'fmt ');
        v.setUint32(16, 16, true); v.setUint16(20, 1, true); v.setUint16(22, 1, true);
        v.setUint32(24, sr, true); v.setUint32(28, sr * 2, true); v.setUint16(32, 2, true); v.setUint16(34, 16, true);
        s(36, 'data'); v.setUint32(40, n * 2, true);
        /* Дубль — готовый файл, который слушают: общий квантователь сайта с дизерингом
           (docs/why/pcm16-quantize.md), а не своё отрезание. */
        var data = new Int16Array(n);
        window.__pcm16Quantizer(true)(pcm, n, data, 0, 1);
        return new Blob([head, data.buffer], { type: 'audio/wav' });
    }

    /* ════ pitch-slide ════ */
    /* data: span (полутона), dir: up|down|updown, deg — откуда начать */
    K.step('pitch-slide', {
        reviewable: false,
        mount: function (host, step, api) {
            var t = T(step);
            if (t.prompt) host.appendChild(api.richEl('p', 'crs-prompt', t.prompt));
            needRange(host, api, function (range) {
                var start = SV.homeOf(range) + (step.deg || 0);
                var want = step.span || 7;
                var big = api.el('p', 'sg-big', api.tt('s_slide_span', { n: 0 }));
                host.appendChild(big);
                var view = PitchView(host, api, { center: start + (step.dir === 'down' ? -want / 2 : want / 2), semis: Math.max(7, Math.ceil(want / 2) + 3), seconds: 8 });
                var status = api.el('p', 'sg-status', '');
                host.appendChild(status);
                var frames = [], raf = 0, done = false;
                var row = api.el('div', 'crs-acts');
                host.appendChild(row);
                refButton(row, api, api.tt('s_listen_start'), function () {
                    return api.audioCtx().then(function (ctx) { SV.tone(ctx, start, ctx.currentTime + 0.05, 1.2); return new Promise(function (r) { setTimeout(r, 1300); }); });
                });
                var deafUntil = 0;
                micPanel(host, api, function (L) {
                    status.textContent = api.tt('s_slide_go', { n: want });
                    var quietSince = 0;
                    voiceStream(L, function (r, tMs) {
                        if (done) return;
                        frames.push({ t: tMs / 1000, midi: r ? r.midi : null });
                        if (frames.length > 2000) frames.shift();
                        view.push(tMs, r ? r.midi : null, true);
                        var j = SV.judgeSlide(frames);
                        big.textContent = api.tt('s_slide_span', { n: Math.round(j.span) });
                        if (!r) { if (!quietSince) quietSince = tMs; if (j.span >= 3 && tMs - quietSince > 1100) finish(j); }
                        else quietSince = 0;
                    }, function (ts) { return ts < deafUntil; });
                    function frame() { if (!api.alive()) return; view.draw(L.ctx.currentTime * 1000); raf = requestAnimationFrame(frame); }
                    raf = requestAnimationFrame(frame);
                    api.onCleanup(function () { cancelAnimationFrame(raf); });
                    api.primary(api.tt('s_done'), function () { finish(SV.judgeSlide(frames)); });
                });
                function finish(j) {
                    if (done) return;
                    done = true;
                    var score = Math.min(1, j.span / want);
                    var lines = [api.tt('s_slide_res', { n: Math.round(j.span), want: want })];
                    if (j.low != null) lines.push(solfa(api, j.low) + ' → ' + solfa(api, j.high));
                    if (j.span >= 3) lines.push(j.smooth >= 0.9 ? api.tt('s_slide_smooth') : api.tt('s_slide_steps'));
                    api.answer({ correct: j.span >= want * 0.8, score: score, explain: lines.join('\n') + (t.explain ? '\n\n' + t.explain : '') });
                }
            });
        }
    });

    /* ════ breath-sustain ════ */
    /* data: seconds (цель); без микрофона — секундомер (честно: «по секундомеру») */
    K.step('breath-sustain', {
        reviewable: false,
        mount: function (host, step, api) {
            var t = T(step);
            var want = step.seconds || 15;
            if (t.prompt) host.appendChild(api.richEl('p', 'crs-prompt', t.prompt));
            var big = api.el('p', 'sg-big sg-secs', dec1(0));
            host.appendChild(big);
            var meter = api.el('div', 'sg-meter');
            var bar = api.el('span');
            meter.appendChild(bar);
            host.appendChild(meter);
            var status = api.el('p', 'sg-status', '');
            host.appendChild(status);
            var done = false;
            micPanel(host, api, function (L) {
                status.textContent = api.tt('s_breath_quiet');
                var calib = [], bm = null, raf = 0, startT = null;
                var unsub = L.onBlock(function (pcm, frame) {
                    if (done) return;
                    var tMs = (frame + pcm.length) / L.sr * 1000;
                    var r = rmsOf(pcm);
                    if (startT == null) startT = tMs;
                    if (!bm) {
                        calib.push(r);
                        if (tMs - startT > 1200) {
                            calib.sort(function (a, b) { return a - b; });
                            var fl = calib[calib.length >> 1];
                            /* Громче −40 dBFS — это не тишина комнаты: человек уже
                               звучит или рядом шумно. Порог от такого «пола» не
                               пустил бы ни один выдох — мерим заново. */
                            if (fl > 0.01) { calib = []; startT = tMs; status.textContent = api.tt('s_breath_noisy'); return; }
                            bm = new SV.BreathMeter(fl);
                            status.textContent = api.tt('s_breath_go', { s: want });
                            api.primary(api.tt('s_done'), function () { finish(bm.best / 1000, bm.stability(), false); });
                        }
                        return;
                    }
                    var st = bm.push(tMs, r);
                    var shown = st.active ? st.dur : 0;
                    big.textContent = dec1(shown / 1000);
                    bar.style.width = Math.max(0, Math.min(100, (20 * Math.log10(r + 1e-9) + 60) / 50 * 100)) + '%';
                    if (st.ended) finish(bm.best / 1000, bm.stability(), false);
                });
                api.onCleanup(unsub);
                api.primary(null, null);
            }, { alt: { label: api.tt('s_stopwatch'), fn: stopwatch } });
            function stopwatch() {
                var t0 = 0, iv = 0;
                status.textContent = api.tt('s_sw_hint');
                api.onCleanup(function () { clearInterval(iv); });
                api.secondary(null, null);
                api.primary(api.tt('s_sw_start'), function () {
                    t0 = performance.now();
                    iv = setInterval(function () { big.textContent = dec1((performance.now() - t0) / 1000); }, 100);
                    api.primary(api.tt('s_sw_stop'), function () { clearInterval(iv); finish((performance.now() - t0) / 1000, null, true); });
                });
            }
            function finish(sec, stab, manual) {
                if (done) return;
                done = true;
                var lines = [api.tt(manual ? 's_breath_res_sw' : 's_breath_res', { s: dec1(sec), want: want })];
                if (stab != null) lines.push(stab <= 3 ? api.tt('s_breath_even') : api.tt('s_breath_uneven', { db: dec1(stab) }));
                var ex = lines.join('\n') + (t.explain ? '\n\n' + t.explain : '');
                if (manual) { api.answer({ correct: null, explain: ex }); return; }
                api.answer({ correct: sec >= want * 0.8, score: Math.min(1, sec / want), explain: ex });
            }
        }
    });

    /* Задержка «щелчок → микрофон услышал слог»: замеренная (калибровка
       гитарного курса в том же браузере пишет её в store курса) — вычесть;
       не замеренная — снять медианой сдвигов самого человека: судим ровность
       ритма, а не то, насколько браузер честно назвал свою задержку. */
    function alignOnsets(expected, onsets, lat) {
        if (lat.measured) return onsets.map(function (x) { return x - lat.s; });
        return SV.alignByMedian(expected, onsets);
    }
    /* ════ voice-rhythm ════ */
    /* data: hits [доли от начала такта, по всем тактам подряд], bars, bpm, syl
       — слог на каждую ноту ритма; без микрофона — касание по долям. */
    K.step('voice-rhythm', {
        reviewable: false,
        mount: function (host, step, api) {
            var t = T(step);
            var bpm = step.bpm || 76, spb = 60 / bpm, bars = step.bars || 2;
            var hits = step.hits || [0, 1, 2, 3];
            if (t.prompt) host.appendChild(api.richEl('p', 'crs-prompt', t.prompt));
            var lane = api.el('div', 'sg-lane');
            host.appendChild(lane);
            var marks = [];
            for (var b = 0; b < bars; b++) hits.forEach(function (h) { marks.push(b * 4 + h); });
            marks.forEach(function (m) { var i = api.el('i'); i.style.left = (m / (bars * 4) * 100) + '%'; lane.appendChild(i); });
            var status = api.el('p', 'sg-status', '');
            host.appendChild(status);
            var mode = null, metro = null, finished = false, taps = [], onsets = [];
            api.onCleanup(function () { if (metro) metro.stop(); });
            micPanel(host, api, function (L) { mode = { kind: 'mic', L: L }; ready(); }, { alt: { label: api.tt('s_tap_mode'), fn: tapMode } });
            function ready() { status.textContent = api.tt('s_rhythm_ready', { bpm: bpm }); api.secondary(null, null); api.primary(api.tt('s_start_ex'), go); }
            function tapMode() {
                api.audioCtx().then(function (ctx) {
                    if (!api.alive()) return;
                    mode = { kind: 'tap', ctx: ctx };
                    var pad = api.el('button', 'sg-pad', api.tt('s_tap_pad'));
                    pad.type = 'button';
                    pad.addEventListener('pointerdown', function (e) { e.preventDefault(); if (metro && !finished) taps.push(root.AudibleClock ? root.AudibleClock.heardAtPerf(ctx, e.timeStamp) : ctx.currentTime); });
                    api.onKey(function (k, e) { if (k === ' ' || k === 'space') { if (metro && !finished) taps.push(root.AudibleClock ? root.AudibleClock.heardAtPerf(ctx, e.timeStamp) : ctx.currentTime); return true; } return false; });
                    host.appendChild(pad);
                    ready();
                });
            }
            function go() {
                api.primary(null, null); api.secondary(null, null);
                taps = []; onsets = []; finished = false;
                Array.prototype.forEach.call(lane.querySelectorAll('b'), function (x) { x.remove(); });
                var ctx = mode.kind === 'mic' ? mode.L.ctx : mode.ctx;
                metro = MC.metronome(ctx, { bpm: bpm, beats: 4, countIn: 4, bars: bars });
                var unsub = mode.kind === 'mic' ? MC.onsetStream(mode.L, function (x) { onsets.push(x); }) : null;
                var t0 = metro.start();
                metro.onBeat(function (i) {
                    if (i < 0) { status.textContent = api.tt('s_count_in') + ' ' + (4 + i + 1); return; }
                    if (i < bars * 4) status.textContent = api.tt('s_bar_n', { n: Math.floor(i / 4) + 1, total: bars });
                    if (i >= bars * 4 && !finished) {
                        finished = true;
                        if (unsub) unsub();
                        metro.stop();
                        var expected = marks.map(function (m) { return t0 + m * spb; });
                        var heard = mode.kind === 'mic' ? alignOnsets(expected, onsets, MC.latency.get(api, mode.L.ctx)) : taps.slice();
                        var r = MC.analysis.judgeRhythm(expected, heard, step.tol || 0.11);
                        r.marks.forEach(function (mk) {
                            if (!mk.hit) return;
                            var y = api.el('b');
                            y.style.left = ((mk.t + mk.err - t0) / (bars * 4 * spb) * 100) + '%';
                            lane.appendChild(y);
                        });
                        var lines = [api.tt('s_rhythm_res', { hits: r.hits, total: r.total })];
                        if (r.hits >= 3) {
                            var ms = Math.round(Math.abs(r.mean) * 1000);
                            lines.push(ms < 30 ? api.tt('s_rhythm_even') : (r.mean < 0 ? api.tt('s_rhythm_early', { ms: ms }) : api.tt('s_rhythm_late', { ms: ms })));
                        }
                        var pass = step.pass || 0.7;
                        status.textContent = lines.join(' ');
                        api.primary(api.tt('s_accept'), function () { api.answer({ correct: r.score >= pass, score: r.score, explain: lines.join('\n') + (t.explain ? '\n\n' + t.explain : '') }); });
                        api.secondary(api.tt('s_again'), go);
                    }
                });
            }
        }
    });
})(typeof window !== 'undefined' ? window : globalThis);
